import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  Building2,
  Download,
  RefreshCw,
  UserPlus,
  BrainCircuit,
  Lock,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import Layout from '../components/Layout';
import ClientForm from '../components/ClientForm';
import { useDebounce } from '../hooks/useDebounce';
import clientService from '../services/clientService';
import authService from '../services/authService';

const ClientManagementPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy] = useState('client_name');
  const [sortOrder] = useState('asc');

  // Form states
  const [formOpen, setFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Menu and selection states
  // eslint-disable-next-line no-unused-vars
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  // Notification states
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Auto-hide notification
  useEffect(() => {
    if (notification.open) {
      const timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, open: false }));
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [notification.open]);

  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Industry options for filtering
  const industryOptions = [
    '汽车制造',
    '电子制造',
    '机械制造',
    '化工',
    '医疗器械',
    '食品加工',
    '纺织',
    '建材',
    '其他'
  ];

  // Get current user and check permissions
  const currentUser = authService.getCurrentUser();
  const canManageClients = ['admin', 'warehouse_manager'].includes(currentUser?.role);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};

      if (debouncedSearchTerm) {
        filters.search = debouncedSearchTerm;
      }
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }
      if (industryFilter !== 'all') {
        filters.industry = industryFilter;
      }

      const response = await clientService.getClients(
        page + 1, // API expects 1-based pagination
        rowsPerPage,
        sortBy,
        sortOrder,
        filters
      );

      if (response.clients) {
        setClients(response.clients);
        setTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setNotification({
        open: true,
        message: '加载客户列表失败',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortBy, sortOrder, debouncedSearchTerm, statusFilter, industryFilter]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleCreateClient = () => {
    setEditingClient(null);
    setFormOpen(true);
  };

  const handleOnboardingClick = () => {
    navigate('/settings/clients/onboarding');
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setFormOpen(true);
  };

  const handleViewClient = (client) => {
    navigate(`/settings/clients/${client._id}`);
  };

  const handleDeleteClient = (client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteClient = async () => {
    try {
      await clientService.deleteClient(clientToDelete._id);
      setNotification({
        open: true,
        message: '客户删除成功',
        severity: 'success'
      });
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      setNotification({
        open: true,
        message: '删除客户失败',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      setFormLoading(true);
      if (editingClient) {
        await clientService.updateClient(editingClient._id, formData);
        setNotification({
          open: true,
          message: '客户信息更新成功',
          severity: 'success'
        });
      } else {
        await clientService.createClient(formData);
        setNotification({
          open: true,
          message: '客户创建成功',
          severity: 'success'
        });
      }
      setFormOpen(false);
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
      throw new Error(error.response?.data?.message || '保存失败');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(clients.map(client => client._id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (clientId) => {
    setSelectedRows(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    try {
      // Update each selected client's status
      await Promise.all(
        selectedRows.map(clientId => {
          const client = clients.find(c => c._id === clientId);
          return clientService.updateClient(clientId, { ...client, status: newStatus });
        })
      );

      setNotification({
        open: true,
        message: `已批量更新 ${selectedRows.length} 个客户的状态`,
        severity: 'success'
      });

      setSelectedRows([]);
      fetchClients();
    } catch (error) {
      console.error('Error updating client status:', error);
      setNotification({
        open: true,
        message: '批量更新失败',
        severity: 'error'
      });
    }
  };

  const handleExportClients = () => {
    setNotification({
      open: true,
      message: '导出功能开发中',
      severity: 'info'
    });
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'pending': return 'outline';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return '活跃';
      case 'inactive': return '非活跃';
      case 'pending': return '待审核';
      default: return status;
    }
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  if (!canManageClients) {
    return (
      <Layout title="客户管理" activeTab="settings">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
          <Alert variant="destructive">
            <AlertDescription>
              您没有权限访问此页面。请联系管理员。
            </AlertDescription>
          </Alert>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="客户管理" activeTab="settings">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
        {/* Notification Toast */}
        {notification.open && (
          <div className="fixed top-4 right-4 z-50">
            <Alert
              variant={notification.severity === 'error' ? 'destructive' : 'default'}
              className={cn(
                'shadow-lg',
                notification.severity === 'success' && 'border-success/50 bg-success/10',
                notification.severity === 'info' && 'border-info/50 bg-info/10'
              )}
            >
              <AlertDescription className={cn(
                notification.severity === 'success' && 'text-success',
                notification.severity === 'info' && 'text-info'
              )}>
                {notification.message}
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-1">
            客户管理
          </h1>
          <p className="text-muted-foreground">
            管理和维护客户信息，配置客户特定设置
          </p>
        </div>

        {/* Actions and Filters */}
        <Card className="border bg-card mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="搜索客户..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="状态筛选" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部状态</SelectItem>
                  <SelectItem value="active">活跃</SelectItem>
                  <SelectItem value="inactive">非活跃</SelectItem>
                  <SelectItem value="pending">待审核</SelectItem>
                </SelectContent>
              </Select>

              <Select value={industryFilter} onValueChange={setIndustryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="行业筛选" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部行业</SelectItem>
                  {industryOptions.map((industry) => (
                    <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex gap-2 flex-wrap justify-end">
                <Button onClick={handleCreateClient}>
                  <Plus className="h-4 w-4 mr-2" />
                  快速新增
                </Button>
                <Button variant="outline" onClick={handleOnboardingClick}>
                  <BrainCircuit className="h-4 w-4 mr-2" />
                  入驻向导
                </Button>
                <Button variant="outline" onClick={handleExportClients}>
                  <Download className="h-4 w-4 mr-2" />
                  导出
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={fetchClients}
                  disabled={loading}
                >
                  <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
                </Button>
              </div>
            </div>

            {/* Bulk Actions Toolbar */}
            {selectedRows.length > 0 && (
              <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                <span className="text-sm text-muted-foreground">
                  已选择 {selectedRows.length} 个客户
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkStatusUpdate('active')}
                >
                  批量激活
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-warning border-warning hover:bg-warning/10"
                  onClick={() => handleBulkStatusUpdate('inactive')}
                >
                  批量停用
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Clients Table */}
        <Card className="border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="w-12 p-3">
                    <Checkbox
                      checked={clients.length > 0 && selectedRows.length === clients.length}
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  <th className="text-left p-3 font-medium text-muted-foreground">客户编号</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">客户名称</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">联系人</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">联系方式</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">行业</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">状态</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">创建时间</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                    </td>
                  </tr>
                ) : clients.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-8">
                      <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                      <h3 className="text-lg font-medium text-muted-foreground mb-2">
                        暂无客户数据
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        开始创建您的第一个客户
                      </p>
                      <div className="flex gap-2 justify-center">
                        <Button onClick={handleCreateClient}>
                          <UserPlus className="h-4 w-4 mr-2" />
                          快速新增
                        </Button>
                        <Button variant="outline" onClick={handleOnboardingClick}>
                          <BrainCircuit className="h-4 w-4 mr-2" />
                          入驻向导
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  clients.map((client) => (
                    <tr
                      key={client._id}
                      className={cn(
                        "border-b hover:bg-muted/50 transition-colors",
                        selectedRows.includes(client._id) && "bg-muted/30"
                      )}
                    >
                      <td className="p-3">
                        <Checkbox
                          checked={selectedRows.includes(client._id)}
                          onCheckedChange={() => handleSelectRow(client._id)}
                        />
                      </td>
                      <td className="p-3">
                        <span className="font-medium">{client.client_id}</span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span>{client.client_name || client.name}</span>
                          {(client.is_confidential || client._is_confidential_display) && (
                            <Badge variant="outline" className="text-warning border-warning gap-1">
                              <Lock className="h-3 w-3" />
                              保密
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        {client.contact_info?.primary_contact || '-'}
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {client.contact_info?.email || client.contact_info?.phone || '-'}
                      </td>
                      <td className="p-3">
                        {client.business_info?.industry || '-'}
                      </td>
                      <td className="p-3">
                        <Badge variant={getStatusVariant(client.status)}>
                          {getStatusLabel(client.status)}
                        </Badge>
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {client.created_at ? new Date(client.created_at).toLocaleDateString('zh-CN') : '-'}
                      </td>
                      <td className="p-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewClient(client)}>
                              <Eye className="h-4 w-4 mr-2" />
                              查看详情
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditClient(client)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              编辑信息
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteClient(client)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              删除客户
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">每页显示</span>
              <Select
                value={String(rowsPerPage)}
                onValueChange={(value) => {
                  setRowsPerPage(parseInt(value, 10));
                  setPage(0);
                }}
              >
                <SelectTrigger className="w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                第 {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, totalCount)} 条，共 {totalCount} 条
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(p => p - 1)}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>确认删除</DialogTitle>
              <DialogDescription>
                确定要删除客户 "{clientToDelete?.client_name}" 吗？
                此操作无法撤销，相关的产品配置和库存数据也会被删除。
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                取消
              </Button>
              <Button variant="destructive" onClick={confirmDeleteClient}>
                删除
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Client Form Dialog */}
        <ClientForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSubmit={handleFormSubmit}
          client={editingClient}
          loading={formLoading}
        />
      </div>
    </Layout>
  );
};

export default ClientManagementPage;
